import { createNoteNode } from '../nodeCreationHelpers'
import { ISimplifiedNode } from '../../components/ingestion/IngestedTree'
import { IUnitLessonParams } from './types'

export function processMaterialElement(
  elem: Element,
  params: IUnitLessonParams,
) {
  return Array.from(elem.children).reduce<ISimplifiedNode[]>((acc, child) => {
    if (child.tagName === 'TABLE') {
      return [...acc, ...processTable(child as HTMLTableElement, params)]
    }
    return acc
  }, [])
}

function processTable(table: HTMLTableElement, params: IUnitLessonParams) {
  const materialData = processTableElement(table, params)
  return materialData.map(({ title, contentItems }) => {
    return createNoteNode({
      title: title,
      value: processTableContentItems(contentItems).outerHTML,
      studentFacing: true,
    })
  })
}

function processTableContentItems(items: TableContentItem[]) {
  return items.reduce((acc, item) => {
    acc.append(createTitleElement(item.title))
    acc.append(createItemTable(item))
    return acc
  }, document.createElement('p'))
}

type TableContentItemItem = { title: string; list?: string }

type TableContentItem = {
  imageUrl?: string | null
  title: string
  items: TableContentItemItem[]
}

type TableContentData = {
  title: string
  contentItems: TableContentItem[]
}

// Convert table element to structured TableContentData
function processTableElement(
  table: HTMLTableElement,
  params: IUnitLessonParams,
) {
  return Array.from(table.rows).reduce<TableContentData[]>((acc, row) => {
    if (!row.cells.length) return acc
    if (row.cells.length === 1) {
      return [
        ...acc,
        {
          title: row.textContent?.trim() ?? '',
          contentItems: [],
        },
      ]
    } else if (!acc.length) {
      acc.push({
        title: '',
        contentItems: [],
      })
    }
    const [imageCell, ...contentCells] = Array.from(row.cells)
    const imgSource = imageCell
      .getElementsByTagName('img')
      .item(0)
      ?.getAttribute('src')
    const contentItem: TableContentItem = {
      items: [],
      imageUrl: getUpdatedIconUrl(imgSource ?? '', params),
      title:
        imageCell
          .getElementsByTagName('figcaption')
          .item(0)
          ?.textContent?.trim() ?? '',
    }

    contentCells.forEach((cell) => {
      Array.from(cell.getElementsByTagName('p')).forEach((item) => {
        if (item.textContent) {
          contentItem.items.push({
            title: item.textContent,
            list: item.nextElementSibling?.outerHTML,
          })
        }
      })
    })
    const lastItem = acc.pop()!
    lastItem.contentItems.push(contentItem)

    return [...acc, lastItem]
  }, [])
}

function createTitleElement(title: string) {
  const titleP = document.createElement('p')
  titleP.style.fontFamily = 'Lexend Deca, sans-serif'
  titleP.style.fontSize = '16px'
  titleP.style.color = '#000000'
  const strong = document.createElement('strong')
  strong.textContent = title
  titleP.append(strong)
  return titleP
}

// Each table has one row, the first cell is the image, the second cell is the content list
function createItemTable(item: TableContentItem) {
  const table = document.createElement('table')
  table.style.width = '100%'
  table.style.borderCollapse = 'collapse'
  const row = table.insertRow()
  addImgCell(item.imageUrl ?? '', row)
  addContentCell(item.items, row)
  return table
}

function addImgCell(imageUrl: string, row: HTMLTableRowElement) {
  if (!imageUrl) {
    insertStudentGuideCell(21, row, document.createElement('span'))
    return
  }
  const img = document.createElement('img')
  img.src = imageUrl
  img.width = 100
  img.height = 100
  img.style.marginLeft = 'auto'
  img.style.marginRight = 'auto'
  const imgSpan = document.createElement('span')
  imgSpan.append(img)
  insertStudentGuideCell(21, row, imgSpan)
}

function addContentCell(
  items: TableContentItemItem[],
  row: HTMLTableRowElement,
) {
  const elements = items.reduce<Element[]>((acc, { title, list }) => {
    const contentList = document.createElement('ul')
    const listItem = document.createElement('li')
    contentList.append(listItem)
    const titleSpan = document.createElement('span')
    titleSpan.textContent = title.replace(/^\d+\.\s*/, '')
    listItem.append(titleSpan)

    if (list) {
      const div = document.createElement('div')
      div.innerHTML = list.trim().replaceAll('margin-left:36pt;', '') ?? ''
      listItem.append(div.firstChild!)
    }
    return [...acc, contentList]
  }, [])
  insertStudentGuideCell(79, row, ...elements)
}

function insertStudentGuideCell(
  width: number,
  row: HTMLTableRowElement,
  ...elements: Element[]
) {
  const cell = row.insertCell()
  cell.style.color = 'black'
  cell.style.width = `${width}%`
  cell.style.borderStyle = 'hidden'
  cell.style.borderWidth = '0px'
  cell.style.verticalAlign = 'top'
  cell.append(...elements)
}

function getUpdatedIconUrl(url: string, params: IUnitLessonParams) {
  const iconId = url.split('/').pop()?.split('.')[0]
  if (!iconId) return url
  const swapWith = params.phenomIconMap[iconId]
  if (!swapWith) {
    return url
  }
  return swapWith
}
